import styled from "styled-components";

import { ShareBarCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Button, FlexColumn, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { TrafficShareWithVisits } from "app/components/React/Table/cells/TrafficShareWithVisits";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, formatShare, spendFilter } from "app/filters/ngFilters";

import { Overlay, OverlayContent } from "../StyledComponents";
import { NA_VALUE, hasAdIntelligenceOverviewAccess, isAdIntelligenceNoTouchPreviewVariant } from "../utils/utils";

export const NO_CELL = { rowSpan: 0, colSpan: 0 };
export const PREVIEW_CELL_SPAN = { rowSpan: 5, colSpan: 2 };

const COLUMN_COUNT = 2;

const PreviewCellWrapper = styled(FlexColumn)`
    width: 100%;
    height: 100%;
    gap: 16px;
`;

const Background = styled.div<{ $columnCount: number }>`
    display: grid;
    grid-template-columns: ${({ $columnCount }) => `repeat(${$columnCount}, 1fr)`};
    position: absolute;
    top: 0;
    width: 100%;
    > div {
        border-bottom: 1px solid ${colorsPalettes.carbon[50]};
        box-sizing: border-box;
        padding-right: 12px;
        ${({ $columnCount }) =>
            $columnCount > 1 &&
            `
            &:nth-child(even) {
                padding-left: 12px;
            }
        `}
    }
`;

const getCell = (isCompareMode: boolean, compareCount: number) => {
    if (isCompareMode) {
        return (
            <Flex
                height="41px"
                width="calc(100% - 12px)"
            >
                <ShareBarCell
                    values={(() => {
                        const randomValues = Array.from({ length: compareCount }).map(() => Math.random());
                        const sum = randomValues.reduce((acc, val) => acc + val, 0);
                        return randomValues.map((val) => val / sum);
                    })()}
                    valueFormatter={(val) => formatShare(val, 1, 0.01, NA_VALUE) as string}
                />
            </Flex>
        );
    }
    return (
        <Flex
            height="41px"
            width="100%"
        >
            <TrafficShareWithVisits
                row={{ TotalVisits: Math.floor(Math.random() * (10000 - 10 + 1)) + 10 }}
                value={Math.random()}
                totalVisitsFilter={() => (props: number) => spendFilter(props, abbrNumberFilter(), 100)}
                undefinedSign={NA_VALUE}
                thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
            />
        </Flex>
    );
};
interface IPreviewCellProps {
    onButtonClick: () => void;
    buttonText: string;
    title: string;
    subtitle: string;
    rowCount: number;
    columnCount?: number;
    isCompareMode?: boolean;
    compareCount?: number;
}

interface IHookOverlayContentProps {
    title: string;
    subtitle: string;
    buttonText: string;
    onButtonClick: () => void;
    isNoTouchVariant: boolean;
    dataAutomation?: string;
}

interface IAdIntelligenceHookButtonProps {
    buttonText: string;
    onButtonClick: () => void;
    isNoTouchVariant: boolean;
    dataAutomation?: string;
}

export const AdIntelligenceHookButton = ({
    buttonText,
    onButtonClick,
    isNoTouchVariant,
    dataAutomation,
}: IAdIntelligenceHookButtonProps) => {
    if (isNoTouchVariant) {
        return (
            <IconButton
                dataAutomation={dataAutomation}
                iconName="locked"
                type="primary"
                iconSize="xs"
                iconColor={colorsPalettes.carbon[0]}
                onClick={onButtonClick}
            >
                {translate("ad.intelligence.overview.preview.no.touch.button")}
            </IconButton>
        );
    }

    return (
        <IconButton
            dataAutomation={dataAutomation}
            iconName="spark"
            type="trial"
            iconSize="xs"
            iconColor={colorsPalettes.carbon[0]}
            onClick={onButtonClick}
        >
            {buttonText}
        </IconButton>
    );
};

export const HookOverlayContent = ({
    title,
    subtitle,
    buttonText,
    onButtonClick,
    isNoTouchVariant,
    dataAutomation,
}: IHookOverlayContentProps) => (
    <OverlayContent>
        <Text
            fontVariant="button"
            color={colorsPalettes.carbon[400]}
        >
            {title}
        </Text>
        <Text
            fontVariant="body2"
            color={colorsPalettes.carbon[400]}
            dangerouslySetInnerHTML={{ __html: subtitle }}
        />
        <AdIntelligenceHookButton
            dataAutomation={dataAutomation}
            buttonText={buttonText}
            isNoTouchVariant={isNoTouchVariant}
            onButtonClick={onButtonClick}
        />
    </OverlayContent>
);

export const PreviewCell = ({
    onButtonClick,
    buttonText,
    title,
    subtitle,
    rowCount,
    columnCount = COLUMN_COUNT,
    isCompareMode = false,
    compareCount = 0,
}: IPreviewCellProps) => {
    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(true);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(true);

    return (
        <PreviewCellWrapper>
            <Background $columnCount={columnCount}>
                {Array.from({ length: rowCount * columnCount }).map((_) => getCell(isCompareMode, compareCount))}
            </Background>
            <Overlay>
                <OverlayContent>
                    <Text
                        fontVariant="button"
                        color={colorsPalettes.carbon[400]}
                    >
                        {title}
                    </Text>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        dangerouslySetInnerHTML={{ __html: subtitle }}
                    />
                    {hasAdIntelBeta ? (
                        <Button
                            width="fit-content"
                            type="primary"
                            onClick={onButtonClick}
                        >
                            {buttonText}
                        </Button>
                    ) : (
                        <AdIntelligenceHookButton
                            dataAutomation="ad-intelligence-overlay-hook-button"
                            buttonText={translate("ad.intelligence.overview.preview.no.access.button")}
                            isNoTouchVariant={isNoTouchVariant}
                            onButtonClick={onButtonClick}
                        />
                    )}
                </OverlayContent>
            </Overlay>
        </PreviewCellWrapper>
    );
};
